// Small previews for catalog images. Lists and media tiles show these instead
// of the originals (up to 10 MB each), which kept Storage egress high.

export const thumbnailMaxSize = 512
// Storage paths are immutable (random UUIDs), so browsers may cache them for a year.
export const immutableCacheControl = "31536000"

export interface ImageThumbnail {
  blob: Blob
  width: number
  height: number
}

export function thumbnailStoragePath(storagePath: string) {
  return `${storagePath}.thumb`
}

export function thumbnailSize(width: number, height: number, maxSize = thumbnailMaxSize) {
  const scale = Math.min(1, maxSize / Math.max(width, height))
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

export function formatResolution(width: number, height: number) {
  return `${width} × ${height} px`
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality))
}

// Returns the thumbnail plus the original dimensions, so the media tile can show
// the original resolution without downloading the original.
export async function createImageThumbnail(file: Blob): Promise<ImageThumbnail> {
  const bitmap = await createImageBitmap(file)
  try {
    const size = thumbnailSize(bitmap.width, bitmap.height)
    const canvas = document.createElement("canvas")
    canvas.width = size.width
    canvas.height = size.height
    const context = canvas.getContext("2d")
    if (!context) throw new Error("Canvas 2D indisponível.")
    context.imageSmoothingQuality = "high"
    context.drawImage(bitmap, 0, 0, size.width, size.height)
    // Browsers without a WebP encoder (Safari) fall back to PNG; use JPEG there to keep it small.
    let blob = await canvasToBlob(canvas, "image/webp", 0.82)
    if (!blob || blob.type !== "image/webp") {
      context.globalCompositeOperation = "destination-over"
      context.fillStyle = "#ffffff"
      context.fillRect(0, 0, size.width, size.height)
      blob = await canvasToBlob(canvas, "image/jpeg", 0.85)
    }
    if (!blob) throw new Error("Não foi possível gerar a miniatura.")
    return { blob, width: bitmap.width, height: bitmap.height }
  } finally {
    bitmap.close()
  }
}
